import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import { DashboardData, DashboardRequest, DashboardResponse } from '../../types/dashboard';

// Async thunk for fetching dashboard data
export const fetchDashboardData = createAsyncThunk(
  'dashboard/fetchData',
  async (request: DashboardRequest) => {
    const response = await fetch('/.netlify/functions/getDashboardData', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(request),
    });
    
    const result: DashboardResponse = await response.json();
    
    if (!result.success) {
      throw new Error(result.error || 'Failed to fetch dashboard data');
    }
    
    return result.data!;
  }
);

interface DashboardState {
  // Data
  data: DashboardData | null;
  
  // Loading states
  isLoading: boolean;
  
  // Error handling
  error: string | null;
  
  // Last updated timestamp
  lastUpdated: string | null;
}

const initialState: DashboardState = {
  data: null,
  isLoading: false,
  error: null,
  lastUpdated: null,
};

const dashboardSlice = createSlice({
  name: 'dashboard',
  initialState,
  reducers: {
    // Clear error
    clearError: (state) => {
      state.error = null;
    },
    
    // Clear dashboard data
    clearData: (state) => {
      state.data = null;
      state.lastUpdated = null;
    },
    
    // Update points (when expenses are added/updated)
    updatePoints: (state, action: PayloadAction<{
      partner1_points: number;
      partner2_points: number;
      total_points: number;
    }>) => {
      if (state.data) {
        state.data.couple.partner1_points = action.payload.partner1_points;
        state.data.couple.partner2_points = action.payload.partner2_points;
        state.data.couple.total_points = action.payload.total_points;
      }
    },
  },
  extraReducers: (builder) => {
    // Fetch dashboard data
    builder
      .addCase(fetchDashboardData.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchDashboardData.fulfilled, (state, action: PayloadAction<DashboardData>) => {
        state.isLoading = false;
        state.data = action.payload;
        state.lastUpdated = new Date().toISOString();
        state.error = null;
      })
      .addCase(fetchDashboardData.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.error.message || 'Failed to fetch dashboard data';
      });
  },
});

export const {
  clearError,
  clearData,
  updatePoints,
} = dashboardSlice.actions;

export default dashboardSlice.reducer;